import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
} from '@mui/material';
import {
  Search as SearchIcon,
  Info as InfoIcon,
  LocalHospital as LocalHospitalIcon,
} from '@mui/icons-material';
import { infectiousDiseases, skinRashes, generalInfectiousDiseases, generalPediatrics, categories } from '../data/atlasData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`atlas-tabpanel-${index}`}
      aria-labelledby={`atlas-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Atlas: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedDisease, setSelectedDisease] = useState<any>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleImageClick = (imageUrl: string, disease: any) => {
    setSelectedDisease(disease);
  };

  const handleCloseDialog = () => {
    setSelectedDisease(null);
  };

  // Данные и категории импортированы из ../data/atlasData.ts

  const currentDiseases = tabValue === 0 ? generalInfectiousDiseases : 
                         tabValue === 1 ? infectiousDiseases : 
                         tabValue === 2 ? skinRashes : generalPediatrics;
  
  const filteredDiseases = currentDiseases.filter(disease => {
    const matchesSearch = disease.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         disease.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         disease.symptoms.some(symptom => symptom.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !selectedCategory || disease.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return '#6B7280';
      case 'medium': return '#2563EB';
      case 'low': return '#059669';
      default: return '#718096';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'high': return 'Высокая';
      case 'medium': return 'Средняя';
      case 'low': return 'Низкая';
      default: return 'Неизвестно';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: { xs: 3, sm: 6 }, px: { xs: 1, sm: 0 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: { xs: 1, sm: 0 },
            mr: { xs: 0, sm: 2 }
          }}>
            <LocalHospitalIcon sx={{ color: '#4A90E2', mr: 2, fontSize: { xs: 32, sm: 40 } }} />
            <Box>
            <Typography variant="h2" sx={{ 
              mb: { xs: 1, sm: 2 }, 
              color: '#2D3748', 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
            }}>
              🏥 Библиотека заболеваний
            </Typography>
            <Typography variant="body1" sx={{ 
              color: '#718096', 
              maxWidth: 800,
              fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              Краткие тезисные статьи о детских инфекционных и кожных заболеваниях с описанием 
              клинической картины, диагностики, лечения и профилактики.
            </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Alert 
        severity="info" 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(74, 144, 226, 0.15)',
          background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
          border: '1px solid #BBDEFB'
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          <strong>📚 Важно:</strong> Данная библиотека предназначена для образовательных целей. 
          Диагностика заболеваний должна проводиться квалифицированным врачом.
        </Typography>
      </Alert>

      <Card sx={{ 
        mb: { xs: 2, sm: 4 }, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                px: { xs: 1, sm: 3 }, 
                '& .MuiTab-root': { 
                  fontWeight: 600, 
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                  minHeight: { xs: 48, sm: 64 },
                  px: { xs: 1, sm: 2 }
                }
              }}
            >
              <Tab 
                label="Инфекционные заболевания" 
                icon={<LocalHospitalIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Инфекционные заболевания с сыпью" 
                icon={<LocalHospitalIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Кожные заболевания" 
                icon={<InfoIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Общая педиатрия" 
                icon={<InfoIcon />} 
                iconPosition="start"
              />
        </Tabs>
      </Box>
        </CardContent>
      </Card>

      <TabPanel value={tabValue} index={0}>
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
          border: '1px solid #E9ECEF'
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 3 }, 
              alignItems: { xs: 'stretch', sm: 'center' }
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#495057', fontWeight: 600, mb: 1 }}>
                  🔍 Поиск заболевания:
                </Typography>
                <TextField
                  placeholder="Введите название заболевания или симптом..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4A90E2'
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#4A90E2' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#495057', fontWeight: 600, mb: 1 }}>
                  📂 Категория:
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4A90E2'
                      }
                    }}
                  >
                    <MenuItem value="">Все категории</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexWrap: 'wrap', 
              mt: 2,
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Chip 
                label={`📋 Найдено: ${filteredDiseases.length}`} 
                sx={{ 
                  backgroundColor: '#4A90E2', 
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}
              />
              <Chip 
                label="📸 Реальные фото" 
                sx={{ 
                  backgroundColor: '#E3F2FD', 
                  color: '#4A90E2',
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}
              />
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)'
          },
          gap: { xs: 2, sm: 3 }
        }}>
          {filteredDiseases.map((disease, index) => (
            <Fade in={true} timeout={300 + index * 100} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: `0 4px 12px ${getSeverityColor(disease.severity)}20`,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: `2px solid ${getSeverityColor(disease.severity)}30`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${getSeverityColor(disease.severity)}40`
                  }
                }}
              >
                <Box sx={{ 
                  p: 3,
                  backgroundColor: '#F8F9FA',
                  borderBottom: '1px solid #E9ECEF'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2
                    }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: getSeverityColor(disease.severity),
                        flexShrink: 0
                      }} />
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        color: '#2D3748',
                        fontSize: '1.1rem'
                      }}>
                        {disease.name}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      flexWrap: 'wrap'
                    }}>
                      <Chip
                        label={getSeverityLabel(disease.severity)}
                        size="small"
                        sx={{
                          backgroundColor: getSeverityColor(disease.severity),
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                      <Chip
                        label={disease.ageGroup}
                        size="small"
                        sx={{
                          backgroundColor: '#E3F2FD',
                          color: '#1976D2',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: '#666',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                    mb: 1
                  }}>
                    {disease.category}
                  </Typography>
                </Box>
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: { xs: 2, sm: 3 }
                }}>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#718096', 
                    mb: 2, 
                    flexGrow: 1, 
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {disease.description}
                  </Typography>
                  
                  {/* Ключевые симптомы */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: '#374151', 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '0.8rem'
                    }}>
                      🔍 Ключевые симптомы:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {disease.symptoms.slice(0, 3).map((symptom: string, i: number) => (
                        <Chip
                          key={i}
                          label={symptom}
                          size="small"
                          sx={{ 
                            backgroundColor: '#F3F4F6', 
                            color: '#374151',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      ))}
                      {disease.symptoms.length > 3 && (
                        <Chip
                          label={`+${disease.symptoms.length - 3} еще`}
                          size="small"
                          sx={{ 
                            backgroundColor: '#E5E7EB', 
                            color: '#374151',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {/* Дополнительная информация */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}>
                    <Chip
                      label={`⏱️ ${disease.incubation}`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#E3F2FD', 
                        color: '#1976D2',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24
                      }}
                    />
                    <Chip
                      label={`🦠 ${disease.contagious}`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#FEF3C7', 
                        color: '#D97706',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<InfoIcon />}
                    onClick={() => handleImageClick('', disease)}
                    sx={{ 
                      alignSelf: 'flex-start',
                      background: `linear-gradient(45deg, ${getSeverityColor(disease.severity)}, ${getSeverityColor(disease.severity)}CC)`,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      fontSize: '0.8rem',
                      '&:hover': {
                        background: `linear-gradient(45deg, ${getSeverityColor(disease.severity)}CC, ${getSeverityColor(disease.severity)}99)`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${getSeverityColor(disease.severity)}40`
                      }
                    }}
                  >
                    📖 Читать статью
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          ))}
        </Box>

        {filteredDiseases.length === 0 && (
          <Fade in={true}>
            <Card sx={{ 
              textAlign: 'center', 
              py: 6, 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)'
            }}>
              <CardContent>
                <SearchIcon sx={{ fontSize: 64, color: '#4A90E2', mb: 2 }} />
                <Typography variant="h5" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
              По вашему запросу ничего не найдено
            </Typography>
                <Typography variant="body1" sx={{ color: '#718096' }}>
                  Попробуйте изменить поисковый запрос или выберите другую категорию
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        )}
      </TabPanel>


      <TabPanel value={tabValue} index={1}>
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                placeholder="Поиск по заболеванию или симптомам..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ 
                  minWidth: 300,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#9C27B0' }} />
                    </InputAdornment>
                  )
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Категория</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Категория"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={`Найдено: ${filteredDiseases.length}`} 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                <Chip 
                  label="Научные статьи" 
                  color="secondary" 
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)'
          },
          gap: { xs: 2, sm: 3 }
        }}>
          {filteredDiseases.map((disease, index) => (
            <Fade in={true} timeout={300 + index * 100} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: `0 4px 12px ${getSeverityColor(disease.severity)}20`,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: `2px solid ${getSeverityColor(disease.severity)}30`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${getSeverityColor(disease.severity)}40`
                  }
                }}
              >
                <Box sx={{ 
                  p: 3,
                  backgroundColor: '#F8F9FA',
                  borderBottom: '1px solid #E9ECEF'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2
                    }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: getSeverityColor(disease.severity),
                        flexShrink: 0
                      }} />
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        color: '#2D3748',
                        fontSize: '1.1rem'
                      }}>
                        {disease.name}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      flexWrap: 'wrap'
                    }}>
                      <Chip
                        label={getSeverityLabel(disease.severity)}
                        size="small"
                        sx={{
                          backgroundColor: getSeverityColor(disease.severity),
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                      <Chip
                        label={disease.ageGroup}
                        size="small"
                        sx={{
                          backgroundColor: '#E3F2FD',
                          color: '#1976D2',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: '#666',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                    mb: 1
                  }}>
                    {disease.category}
                  </Typography>
                </Box>
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: { xs: 2, sm: 3 }
                }}>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#718096', 
                    mb: 2, 
                    flexGrow: 1, 
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {disease.description}
                  </Typography>
                  
                  {/* Ключевые симптомы */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: '#374151', 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '0.8rem'
                    }}>
                      🔍 Ключевые симптомы:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {disease.symptoms.slice(0, 3).map((symptom: string, i: number) => (
                        <Chip
                          key={i}
                          label={symptom}
                          size="small"
                          sx={{ 
                            backgroundColor: '#F3F4F6', 
                            color: '#374151',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      ))}
                      {disease.symptoms.length > 3 && (
                        <Chip
                          label={`+${disease.symptoms.length - 3} еще`}
                          size="small"
                          sx={{ 
                            backgroundColor: '#E5E7EB', 
                            color: '#374151',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {/* Дополнительная информация */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}>
                    <Chip
                      label={`⏱️ ${disease.incubation}`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#E3F2FD', 
                        color: '#1976D2',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24
                      }}
                    />
                    <Chip
                      label={`🦠 ${disease.contagious}`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#FEF3C7', 
                        color: '#D97706',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<InfoIcon />}
                    onClick={() => handleImageClick('', disease)}
                    sx={{ 
                      alignSelf: 'flex-start',
                      background: `linear-gradient(45deg, ${getSeverityColor(disease.severity)}, ${getSeverityColor(disease.severity)}CC)`,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      fontSize: '0.8rem',
                      '&:hover': {
                        background: `linear-gradient(45deg, ${getSeverityColor(disease.severity)}CC, ${getSeverityColor(disease.severity)}99)`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${getSeverityColor(disease.severity)}40`
                      }
                    }}
                  >
                    📖 Читать статью
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
          border: '1px solid #E9ECEF'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 3 }, 
              alignItems: { xs: 'stretch', sm: 'center' }
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#495057', fontWeight: 600, mb: 1 }}>
                  🔍 Поиск заболевания:
                </Typography>
                <TextField
                  placeholder="Введите название заболевания или симптом..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4A90E2'
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#4A90E2' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#495057', fontWeight: 600, mb: 1 }}>
                  📂 Категория:
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4A90E2'
                      }
                    }}
                  >
                    <MenuItem value="">Все категории</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexWrap: 'wrap', 
              mt: 2,
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Chip 
                label={`Найдено: ${filteredDiseases.length}`} 
                color="primary" 
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              <Chip 
                label="Научные статьи" 
                color="secondary" 
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)'
          },
          gap: { xs: 2, sm: 3 }
        }}>
          {filteredDiseases.map((disease, index) => (
            <Fade in={true} timeout={300 + index * 100} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: `0 4px 12px ${getSeverityColor(disease.severity)}20`,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: `2px solid ${getSeverityColor(disease.severity)}30`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${getSeverityColor(disease.severity)}40`
                  }
                }}
              >
                <Box sx={{ 
                  p: 3,
                  backgroundColor: '#F8F9FA',
                  borderBottom: '1px solid #E9ECEF'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2
                    }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: getSeverityColor(disease.severity),
                        flexShrink: 0
                      }} />
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        color: '#2D3748',
                        fontSize: '1.1rem'
                      }}>
                        {disease.name}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      flexWrap: 'wrap'
                    }}>
                      <Chip
                        label={getSeverityLabel(disease.severity)}
                        size="small"
                        sx={{
                          backgroundColor: getSeverityColor(disease.severity),
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                      <Chip
                        label={disease.ageGroup}
                        size="small"
                        sx={{
                          backgroundColor: '#E3F2FD',
                          color: '#1976D2',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: '#666',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                    mb: 1
                  }}>
                    {disease.category}
                  </Typography>
                </Box>
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: { xs: 2, sm: 3 }
                }}>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#718096', 
                    mb: 2, 
                    flexGrow: 1, 
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {disease.description}
                  </Typography>
                  
                  {/* Ключевые симптомы */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: '#374151', 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '0.8rem'
                    }}>
                      🔍 Ключевые симптомы:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {disease.symptoms.slice(0, 3).map((symptom: string, i: number) => (
                        <Chip
                          key={i}
                          label={symptom}
                          size="small"
                          sx={{ 
                            backgroundColor: '#F3F4F6', 
                            color: '#374151',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      ))}
                      {disease.symptoms.length > 3 && (
                        <Chip
                          label={`+${disease.symptoms.length - 3} еще`}
                          size="small"
                          sx={{ 
                            backgroundColor: '#E5E7EB', 
                            color: '#374151',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {/* Дополнительная информация */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}>
                    <Chip
                      label={`⏱️ ${disease.incubation}`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#E3F2FD', 
                        color: '#1976D2',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24
                      }}
                    />
                    <Chip
                      label={`🦠 ${disease.contagious}`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#FEF3C7', 
                        color: '#D97706',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<InfoIcon />}
                    onClick={() => handleImageClick('', disease)}
                    sx={{ 
                      alignSelf: 'flex-start',
                      background: `linear-gradient(45deg, ${getSeverityColor(disease.severity)}, ${getSeverityColor(disease.severity)}CC)`,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      fontSize: '0.8rem',
                      '&:hover': {
                        background: `linear-gradient(45deg, ${getSeverityColor(disease.severity)}CC, ${getSeverityColor(disease.severity)}99)`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${getSeverityColor(disease.severity)}40`
                      }
                    }}
                  >
                    📖 Читать статью
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
          border: '1px solid #E9ECEF'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 3 }, 
              alignItems: { xs: 'stretch', sm: 'center' }
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#495057', fontWeight: 600, mb: 1 }}>
                  🔍 Поиск заболевания:
                </Typography>
                <TextField
                  placeholder="Введите название заболевания или симптом..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4A90E2'
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#4A90E2' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#495057', fontWeight: 600, mb: 1 }}>
                  📂 Категория:
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4A90E2'
                      }
                    }}
                  >
                    <MenuItem value="">Все категории</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexWrap: 'wrap', 
              mt: 2,
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Chip 
                label={`Найдено: ${filteredDiseases.length}`} 
                color="primary" 
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              <Chip 
                label="Научные статьи" 
                color="secondary" 
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)'
          },
          gap: { xs: 2, sm: 3 }
        }}>
          {filteredDiseases.map((disease, index) => (
            <Fade in={true} timeout={300 + index * 100} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: `0 4px 12px ${getSeverityColor(disease.severity)}20`,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: `2px solid ${getSeverityColor(disease.severity)}30`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${getSeverityColor(disease.severity)}40`
                  }
                }}
              >
                <Box sx={{ 
                  p: 3,
                  backgroundColor: '#F8F9FA',
                  borderBottom: '1px solid #E9ECEF'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2
                    }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: getSeverityColor(disease.severity),
                        flexShrink: 0
                      }} />
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        color: '#2D3748',
                        fontSize: '1.1rem'
                      }}>
                        {disease.name}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      flexWrap: 'wrap'
                    }}>
                      <Chip
                        label={getSeverityLabel(disease.severity)}
                        size="small"
                        sx={{
                          backgroundColor: getSeverityColor(disease.severity),
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                      <Chip
                        label={disease.ageGroup}
                        size="small"
                        sx={{
                          backgroundColor: '#E3F2FD',
                          color: '#1976D2',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: '#666',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                    mb: 1
                  }}>
                    {disease.category}
                  </Typography>
                </Box>
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: { xs: 2, sm: 3 }
                }}>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#718096', 
                    mb: 2, 
                    flexGrow: 1, 
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {disease.description}
                  </Typography>
                  
                  {/* Ключевые симптомы */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: '#374151', 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '0.8rem'
                    }}>
                      🔍 Ключевые симптомы:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {disease.symptoms.slice(0, 3).map((symptom: string, i: number) => (
                        <Chip
                          key={i}
                          label={symptom}
                          size="small"
                          sx={{ 
                            backgroundColor: '#F3F4F6', 
                            color: '#374151',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      ))}
                      {disease.symptoms.length > 3 && (
                        <Chip
                          label={`+${disease.symptoms.length - 3} еще`}
                          size="small"
                          sx={{ 
                            backgroundColor: '#E5E7EB', 
                            color: '#374151',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {/* Дополнительная информация */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}>
                    <Chip
                      label={`⏱️ ${disease.incubation}`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#E3F2FD', 
                        color: '#1976D2',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24
                      }}
                    />
                    <Chip
                      label={`🦠 ${disease.contagious}`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#FEF3C7', 
                        color: '#D97706',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<InfoIcon />}
                    onClick={() => handleImageClick('', disease)}
                    sx={{ 
                      alignSelf: 'flex-start',
                      background: `linear-gradient(45deg, ${getSeverityColor(disease.severity)}, ${getSeverityColor(disease.severity)}CC)`,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      fontSize: '0.8rem',
                      '&:hover': {
                        background: `linear-gradient(45deg, ${getSeverityColor(disease.severity)}CC, ${getSeverityColor(disease.severity)}99)`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${getSeverityColor(disease.severity)}40`
                      }
                    }}
                  >
                    📖 Читать статью
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          ))}
        </Box>
      </TabPanel>

      {/* Диалог для просмотра изображений */}
      <Dialog
        open={!!selectedDisease}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
          color: 'white',
          fontWeight: 600,
          p: 3
        }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {selectedDisease?.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {selectedDisease?.category} • {selectedDisease?.ageGroup}
            </Typography>
          </Box>
          <Button onClick={handleCloseDialog} sx={{ color: 'white', minWidth: 'auto' }}>
            ✕
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative', maxHeight: '80vh', overflowY: 'auto' }}>
          {selectedDisease && (
            <Box sx={{ p: 4 }}>
              {/* Описание заболевания */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  📋 Описание заболевания:
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#333', 
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: '1rem'
                }}>
                  {selectedDisease.description}
                </Typography>
              </Box>

              {/* Клиническая картина */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  🩺 Клиническая картина:
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#333', 
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: '0.95rem'
                }}>
                  {selectedDisease.clinicalPresentation}
                </Typography>
              </Box>

              {/* Симптомы */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  🚨 Симптомы:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedDisease.symptoms.map((symptom: string, i: number) => (
                    <Chip
                      key={i}
                      label={symptom}
                      size="small"
                      sx={{ 
                        backgroundColor: '#F3F4F6', 
                        color: '#374151',
                        fontSize: '0.8rem'
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Лечение */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  💊 Лечение:
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#333', 
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: '0.95rem'
                }}>
                  {selectedDisease.treatment}
                </Typography>
              </Box>

              {/* Профилактика */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  🛡️ Профилактика:
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#333', 
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: '0.95rem'
                }}>
                  {selectedDisease.prevention}
                </Typography>
              </Box>

              {/* Осложнения */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  ⚠️ Осложнения:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedDisease.complications.map((complication: string, i: number) => (
                    <Chip
                      key={i}
                      label={complication}
                      size="small"
                      sx={{ 
                        backgroundColor: '#FEF2F2', 
                        color: '#991B1B',
                        fontSize: '0.8rem'
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Диагностика */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  🔬 Диагностика:
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#333', 
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: '0.95rem'
                }}>
                  {selectedDisease.diagnosis}
                </Typography>
              </Box>

              {/* Дифференциальная диагностика */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  🔍 Дифференциальная диагностика:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedDisease.differentialDiagnosis.map((diagnosis: string, i: number) => (
                    <Chip
                      key={i}
                      label={diagnosis}
                      size="small"
                      sx={{ 
                        backgroundColor: '#E3F2FD', 
                        color: '#1976D2',
                        fontSize: '0.8rem'
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Прогноз */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  📈 Прогноз:
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#333', 
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: '0.95rem'
                }}>
                  {selectedDisease.prognosis}
                </Typography>
              </Box>

              {/* Источники */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ 
                  color: '#4A90E2', 
                  fontWeight: 600, 
                  mb: 2 
                }}>
                  📚 Источники:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {selectedDisease.references.map((reference: string, i: number) => (
                    <Typography key={i} variant="body2" sx={{ 
                      color: '#666', 
                      fontSize: '0.8rem',
                      fontStyle: 'italic'
                    }}>
                      • {reference}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, background: '#F8F9FA' }}>
          <Button 
            onClick={handleCloseDialog}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #4A90E2, #357ABD)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4
            }}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Atlas;
